import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trackAddToCart } from "@/lib/tracking";

const CartContext = createContext(null);
const STORAGE_KEY = "hg_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, color = null, qty = 1) => {
    const cash = product.promo_price && product.promo_price > 0 && product.promo_price < product.price
      ? product.promo_price
      : product.price;
    const card = product.price_card && product.price_card > 0 ? product.price_card : product.price;
    setItems((prev) => {
      const key = (i) => `${i.id}::${i.color || ""}`;
      const existing = prev.find((i) => key(i) === key({ id: product.id, color }));
      if (existing) {
        return prev.map((i) => (key(i) === key(existing) ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          image: product.images?.[0] || "",
          price: cash,
          price_card: card,
          max_installments: product.max_installments || 12,
          color,
          qty,
        },
      ];
    });
    trackAddToCart(product, qty);
    toast.success("Adicionado ao carrinho", { description: product.name });
  };

  const removeItem = (id, color) =>
    setItems((prev) => prev.filter((i) => !(i.id === id && (i.color || "") === (color || ""))));

  const setQty = (id, color, qty) => {
    if (qty < 1) return removeItem(id, color);
    setItems((prev) =>
      prev.map((i) => (i.id === id && (i.color || "") === (color || "") ? { ...i, qty } : i))
    );
  };

  const clearCart = () => setItems([]);

  const { count, subtotal } = useMemo(
    () => ({
      count: items.reduce((s, i) => s + i.qty, 0),
      subtotal: items.reduce((s, i) => s + i.price * i.qty, 0),
    }),
    [items]
  );

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, setQty, clearCart, count, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
